<script setup lang="ts">
import { computed, inject, type Ref } from "vue";
import { useRoute } from "vue-router";
import type { MenuItem } from "primevue/menuitem";
import type { PanelMenuExpandedKeys } from "primevue/panelmenu";

import { isSidebarLinkActive } from "@/composables/sidebar-nav-active";

const props = defineProps<{ item: MenuItem }>();

const route = useRoute();

// Provided by SidebarMenu so we can hide this group's aggregated badge once
// the panel is expanded — children render their own badges then.
const expandedMenuKeys = inject< Ref< PanelMenuExpandedKeys > | undefined >( 'sidebarExpandedMenuKeys' );

const isExpanded = computed< boolean >( () => {
  const key = props.item.key;
  if ( !key || !expandedMenuKeys?.value ) return false;
  return !!expandedMenuKeys.value[ key ];
} );

const unreadCount = computed< number >( () => ( props.item as { unreadCount?: number } ).unreadCount ?? 0 );
const showCollapsedBadge = computed< boolean >( () => unreadCount.value > 0 && !isExpanded.value );

const isGroupActive = computed(() => {
  const children = props.item.items?.filter((c) => c.visible !== false && c.route) ?? [];
  return children.some((c) => isSidebarLinkActive(route.path, c.route!));
});

const rowClass = computed(() => [
  "box-border flex min-h-11 w-full min-w-0 cursor-pointer select-none items-center gap-2.5 border-b-[0.5px] border-l-[3px] border-solid border-b-base-content/[0.07] border-l-transparent px-2 py-2.5 text-left text-base font-medium text-base-content transition-colors duration-150 hover:bg-primary/10 dark:hover:bg-primary/15 touch-manipulation",
]);
</script>

<template>
  <div :class="rowClass">
    <template v-if="props.item.image">
      <img :src="props.item.image" class="size-5 shrink-0 rounded" alt="" />
    </template>
    <template v-else-if="props.item.icon">
      <i
        :class="[
          'shrink-0 p-menu-item-icon pi',
          props.item.icon,
          isGroupActive ? 'text-primary' : 'text-primary/80',
        ]"
      />
    </template>
    <div class="min-w-0 truncate leading-snug">{{ props.item.label }}</div>
    <span
      v-if="showCollapsedBadge"
      class="ml-auto inline-flex min-w-5 shrink-0 items-center justify-center rounded-full bg-red-600 px-1.5 py-0.5 text-[0.65rem] font-bold leading-none text-white shadow-sm"
      :aria-label="`${ unreadCount } new items`"
    >
      {{ unreadCount > 99 ? '99+' : unreadCount }}
    </span>
    <span
      v-if="props.item.items?.length"
      class="pi pi-angle-down shrink-0 text-primary"
      :class="showCollapsedBadge ? 'ml-1.5' : 'ml-auto'"
    />
  </div>
</template>
